"use client";

import React from "react";
import ReactMarkdown from "react-markdown";

/**
 * Markdown renderer with theme-encapsulated styling for links, code, etc.
 */
export default function AgentMarkdownContent({
  content,
  className,
  trailing,
}: {
  content: string;
  className?: string;
  /**
   * Optional node rendered right after the markdown. When present, the final
   * paragraph is laid out inline so the trailing node (e.g. a Sources chip)
   * flows after the last word instead of dropping onto its own line.
   */
  trailing?: React.ReactNode;
}) {
  // Only collapse the last paragraph to inline when we have something to trail
  // it with; otherwise leave normal block flow untouched.
  const lastParagraphClass = trailing
    ? "[&:last-of-type]:mb-0 [&:last-of-type]:inline"
    : "last:mb-0";
  return (
    <div
      className={`markdown-content text-inherit [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 ${className ?? ""}`}
    >
      <ReactMarkdown
        components={{
          p: ({ children }) => (
            <p className={`mb-2 ${lastParagraphClass} text-inherit`}>
              {children}
            </p>
          ),
          strong: ({ children }) => (
            <strong className="font-semibold text-inherit">{children}</strong>
          ),
          em: ({ children }) => (
            <em className="italic text-inherit">{children}</em>
          ),
          ul: ({ children }) => (
            <ul className="list-disc list-inside my-2 space-y-1 ml-2">
              {children}
            </ul>
          ),
          ol: ({ children }) => (
            <ol className="list-decimal list-inside my-2 space-y-1 ml-2">
              {children}
            </ol>
          ),
          li: ({ children }) => <li className="text-inherit">{children}</li>,
          a: ({ href, children }) => (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:text-primary underline"
            >
              {children}
            </a>
          ),
          code: ({ children, className }) => {
            const isInline = !className;
            return isInline ? (
              <code className="bg-muted/70 px-1.5 py-0.5 rounded text-xs font-mono text-foreground">
                {children}
              </code>
            ) : (
              <code className="text-inherit">{children}</code>
            );
          },
          pre: ({ children }) => (
            <pre className="bg-muted/70 p-2 rounded overflow-x-auto my-2 text-xs text-foreground">
              <code className="text-inherit">{children}</code>
            </pre>
          ),
          h1: ({ children }) => (
            <h1 className="text-lg font-bold mb-2 mt-3 first:mt-0 text-inherit">
              {children}
            </h1>
          ),
          h2: ({ children }) => (
            <h2 className="text-base font-bold mb-2 mt-3 first:mt-0 text-inherit">
              {children}
            </h2>
          ),
          h3: ({ children }) => (
            <h3 className="text-sm font-semibold mb-1 mt-2 first:mt-0 text-inherit">
              {children}
            </h3>
          ),
        }}
      >
        {content}
      </ReactMarkdown>
      {trailing}
    </div>
  );
}
